import { useMemo } from 'react';

/**
 * Provider color palette for consistent display across views.
 * Uses colors that work well in both light and dark themes.
 */
export const PROVIDER_COLORS = [
  {
    bg: 'bg-blue-500/10 dark:bg-blue-500/20',
    border: 'border-l-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
    dot: 'bg-blue-500',
  },
  {
    bg: 'bg-violet-500/10 dark:bg-violet-500/20',
    border: 'border-l-violet-500',
    text: 'text-violet-600 dark:text-violet-400',
    dot: 'bg-violet-500',
  },
  {
    bg: 'bg-emerald-500/10 dark:bg-emerald-500/20',
    border: 'border-l-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  {
    bg: 'bg-amber-500/10 dark:bg-amber-500/20',
    border: 'border-l-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
  {
    bg: 'bg-rose-500/10 dark:bg-rose-500/20',
    border: 'border-l-rose-500',
    text: 'text-rose-600 dark:text-rose-400',
    dot: 'bg-rose-500',
  },
  {
    bg: 'bg-cyan-500/10 dark:bg-cyan-500/20',
    border: 'border-l-cyan-500',
    text: 'text-cyan-600 dark:text-cyan-400',
    dot: 'bg-cyan-500',
  },
  {
    bg: 'bg-orange-500/10 dark:bg-orange-500/20',
    border: 'border-l-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
    dot: 'bg-orange-500',
  },
  {
    bg: 'bg-indigo-500/10 dark:bg-indigo-500/20',
    border: 'border-l-indigo-500',
    text: 'text-indigo-600 dark:text-indigo-400',
    dot: 'bg-indigo-500',
  },
  {
    bg: 'bg-pink-500/10 dark:bg-pink-500/20',
    border: 'border-l-pink-500',
    text: 'text-pink-600 dark:text-pink-400',
    dot: 'bg-pink-500',
  },
  {
    bg: 'bg-teal-500/10 dark:bg-teal-500/20',
    border: 'border-l-teal-500',
    text: 'text-teal-600 dark:text-teal-400',
    dot: 'bg-teal-500',
  },
] as const;

export type ProviderColor = (typeof PROVIDER_COLORS)[number];

interface Provider {
  id: string;
  firstName: string;
  lastName: string;
}

/**
 * Hook to consistently assign colors to providers.
 * Colors are assigned based on provider ID order to maintain consistency.
 */
export function useProviderColors(providers: Provider[]): Map<string, ProviderColor> {
  return useMemo(() => {
    const colorMap = new Map<string, ProviderColor>();
    providers.forEach((provider, index) => {
      colorMap.set(provider.id, PROVIDER_COLORS[index % PROVIDER_COLORS.length]!);
    });
    return colorMap;
  }, [providers]);
}

/**
 * Get a provider color by index (for cases without the hook).
 */
export function getProviderColor(index: number): ProviderColor {
  return PROVIDER_COLORS[index % PROVIDER_COLORS.length]!;
}
